const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const STORAGE_KEY = 'tax-insight-admin-auth';

export interface AdminUser {
  id: string;
  loginName: string;
  displayName: string | null;
  role: string;
  status?: string;
}

export interface StoredAdminAuth {
  token: string;
  admin: AdminUser;
}

export interface AdminQuota {
  preliminaryLimit: number;
  preliminaryUsed: number;
  preliminaryRemaining: number;
}

export interface AdminManagedUser {
  id: string;
  phoneMasked: string;
  status: 'active' | 'disabled' | string;
  quota: AdminQuota;
  createdAt: string | null;
}

export interface AdminAuditLogItem {
  id: string;
  tenantId: string;
  actorId: string;
  action: string;
  resourceType: string;
  resourceId: string;
  result: string;
  requestId: string;
  createdAt: string | null;
}

export interface AdminAnalysisRecordSummary {
  id: number;
  clientId: string;
  companyName: string;
  industry: string | null;
  region: string | null;
  opportunityScore: number | null;
  createdAt: string | null;
}

export class AdminApiError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'AdminApiError';
    this.code = code;
    this.status = status;
  }
}

export function readStoredAdminAuth(): StoredAdminAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAdminAuth;
    if (!parsed?.token || !parsed?.admin?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredAdminAuth(value: StoredAdminAuth): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function clearStoredAdminAuth(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function adminAuthHeaders(): Record<string, string> {
  const stored = readStoredAdminAuth();
  if (stored?.token) {
    return { Authorization: `Bearer ${stored.token}` };
  }
  return {};
}

async function readAdminError(res: Response, fallback: string): Promise<AdminApiError> {
  const body = await res.json().catch(() => ({}));
  const message = body?.error?.message || fallback;
  const code = body?.error?.code || 'UNKNOWN';
  return new AdminApiError(message, code, res.status);
}

type AdminFetchInit = {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
};

async function adminFetch<T>(path: string, init?: AdminFetchInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: init?.method,
    body: init?.body,
    headers: {
      'Content-Type': 'application/json',
      ...adminAuthHeaders(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) throw await readAdminError(res, '请求失败，请稍后重试');
  const json = await res.json();
  return json.data as T;
}

export async function adminLogin(loginName: string, password: string): Promise<StoredAdminAuth> {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/auth/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ loginName, password }),
  });
  if (!res.ok) throw await readAdminError(res, '登录失败');
  const json = await res.json();
  return json.data as StoredAdminAuth;
}

export async function fetchAdminMe(): Promise<AdminUser> {
  return adminFetch<AdminUser>('/api/v1/admin/auth/me');
}

export async function adminLogout(): Promise<void> {
  try {
    await adminFetch<{ ok: boolean }>('/api/v1/admin/auth/session', { method: 'DELETE' });
  } finally {
    clearStoredAdminAuth();
  }
}

export async function listAdminUsers(params: {
  q?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ items: AdminManagedUser[]; page: number; pageSize: number; total: number }> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.status) sp.set('status', params.status);
  if (params.page) sp.set('page', String(params.page));
  if (params.pageSize) sp.set('pageSize', String(params.pageSize));
  const qs = sp.toString();
  return adminFetch(`/api/v1/admin/users${qs ? `?${qs}` : ''}`);
}

export async function getAdminUser(id: string): Promise<AdminManagedUser> {
  return adminFetch(`/api/v1/admin/users/${id}`);
}

export async function patchUserStatus(
  id: string,
  status: 'active' | 'disabled',
  reason: string,
): Promise<AdminManagedUser> {
  return adminFetch(`/api/v1/admin/users/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
}

export async function patchUserQuota(
  id: string,
  body: { preliminaryLimit?: number; resetUsed?: boolean; reason: string },
): Promise<AdminManagedUser> {
  return adminFetch(`/api/v1/admin/users/${id}/quota`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function listAdminAuditLogs(params: {
  actorId?: string;
  action?: string;
  resourceId?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ items: AdminAuditLogItem[]; page: number; pageSize: number; total: number }> {
  const sp = new URLSearchParams();
  if (params.actorId) sp.set('actorId', params.actorId);
  if (params.action) sp.set('action', params.action);
  if (params.resourceId) sp.set('resourceId', params.resourceId);
  if (params.page) sp.set('page', String(params.page));
  if (params.pageSize) sp.set('pageSize', String(params.pageSize));
  const qs = sp.toString();
  return adminFetch(`/api/v1/admin/audit-logs${qs ? `?${qs}` : ''}`);
}

export async function listUserAnalysisRecords(userId: string): Promise<{
  user: { id: string; phoneMasked: string; status: string };
  items: AdminAnalysisRecordSummary[];
}> {
  return adminFetch(`/api/v1/admin/users/${userId}/analysis-records`);
}
